// De gedeelde sleutel waarmee twee organisaties kunnen zien dat ze bij dezelfde
// voordeur zijn geweest — zonder iets over de bewoner uit te wisselen.
//
// VORM
//   <BAG-id>:<episodenummer>      bijv. '0202010000279244:1'
//
// De sleutel bevat GEEN persoonsgegevens: geen naam, geen e-mailadres, geen
// telefoonnummer. Alleen het landelijke nummer dat elke woning heeft
// (`Location.BAGid`, gevuld door PDOK — ADR-023) en een volgnummer.
//
// WAAROM HET PROGRAMMA ER NIET IN ZIT
// Het raamwerk stelde `<programma>:<BAG-id>:<episode>` voor. Dat werkt niet:
// gemeten 2026-09-03 hangen 11 omgevingen onder MEER DAN ÉÉN portfolio —
// BuurtKlusBedrijf onder drie. Eén huishouden zou dan drie sleutels dragen, en
// elke wijziging in het lidmaatschap zou elk adres moeten herschrijven. Het
// programma hoort op de deelname-rij (`HouseholdParticipations.Programme`),
// waar hij per betrokkenheid geldt in plaats van per woning.
//
// WAAROM LIEVER GEEN SLEUTEL DAN EEN GOK
// Zonder BAG-id blijft de sleutel LEEG. Terugvallen op postcode + huisnummer
// lijkt behulpzaam maar voegt twee woningen in hetzelfde pand samen tot één
// huishouden — en dat is één nulmeting te weinig, wat achteraf niet te
// herstellen is. Een huishouden zonder sleutel doet gewoon niet mee aan de
// koppeling, en dat hoort zichtbaar te zijn.
//
// AI-CONTEXT: See docs/features/energiearmoede-arnhem-ketensamenwerking.md

import sentinelToEmpty from '../sentinelValue'

/** Voor de pilot draagt elk huishouden episode 1. */
const EPISODE_DEFAULT = 1

/**
 * '' TELT HIER ALS SENTINEL, bewust: een leeg BAG-id is geen sleutel. De oude
 * lijst had '' er al in staan en gaf er hetzelfde voor terug. Een woning zonder
 * id doet niet mee aan de koppeling — zie 'WAAROM LIEVER GEEN SLEUTEL DAN EEN
 * GOK' hierboven.
 *
 * Een BAG-id dat ooit als getal is weggeschreven is net zo geldig; alleen de
 * vorm verschilt. `sentinelToEmpty` coerceert daarom in plaats van te weigeren.
 *
 * @param {*} raw - `Location.BAGid` zoals hij in Firestore staat (tekst of getal)
 * @returns {string} Het genormaliseerde BAG-id, of '' als het er niet is
 */
const normalizeBagId = (raw) => sentinelToEmpty(raw)

/**
 * De sleutel van één huishouden.
 *
 * @param {{BAGid?: string|number, GeocodeStatus?: string}} [location] - `Adresses.Location`
 * @param {number} [episodeNr] - Volgnummer van het traject op dit adres; standaard 1
 * @returns {string} De sleutel, of '' als er geen betrouwbare sleutel te maken is
 */
const buildHouseholdKey = (location, episodeNr = EPISODE_DEFAULT) => {
    if (!location || typeof location !== 'object') return ''

    // Een mislukte geocodering betekent dat we de woning niet hebben kunnen
    // vaststellen. Een BAG-id dat daar toch naast staat is niet te vertrouwen.
    if (location.GeocodeStatus === 'failed') return ''

    const bagId = normalizeBagId(location.BAGid)
    if (!bagId) return ''

    // Een episodenummer dat geen positief geheel getal is, is een fout in de
    // aanroep. Er stilzwijgend 1 van maken zou twee trajecten samenvoegen.
    if (!Number.isInteger(episodeNr) || episodeNr < 1) return ''

    return `${bagId}:${episodeNr}`
}

/**
 * Of twee sleutels dezelfde woning én hetzelfde traject aanwijzen.
 *
 * Bewust geen kale `===`: twee LEGE sleutels zijn niet "hetzelfde huishouden"
 * maar twee keer "onbekend". Zonder deze regel worden alle nog niet
 * gegeocodeerde adressen van alle organisaties stilletjes één huishouden.
 *
 * @param {string} [a]
 * @param {string} [b]
 * @returns {boolean}
 */
const sameHousehold = (a, b) => {
    if (!a || !b) return false
    return a === b
}

export default buildHouseholdKey
export { EPISODE_DEFAULT, sameHousehold, normalizeBagId }
